/**
 * Lógica pura de daño de combate: golpes del héroe (ataque%, crítico, doble
 * ataque, proyectiles extra) y golpes de enemigos (fallo, bloqueo,
 * armadura), más el cálculo de robo de vida. Sin Phaser — testeado con
 * Vitest junto a este archivo.
 *
 * El generador aleatorio se recibe siempre inyectado (`rng: () => number`,
 * rango [0,1)) para que todo sea determinista y testeable; en el juego real
 * se pasa `() => rng.float()` de una instancia de `core/rng.ts`.
 */

import type { RunStats } from '../../core/types';

/** Multiplicador de daño de un golpe crítico. */
export const CRIT_MULTIPLIER = 1.5;

export interface HeroHit {
  damage: number;
  isCrit: boolean;
}

export interface HeroAttackResult {
  hits: HeroHit[];
  totalDamage: number;
}

/** Proyectiles que dispara el héroe por ciclo de ataque (1 base + bonus de cartas/gear). */
export function projectileCount(stats: RunStats): number {
  return 1 + Math.max(0, Math.floor(stats.bonusProjectiles));
}

/**
 * Tira un golpe individual del héroe: aplica `atkPct` + `nextWaveAtkPct`
 * (buff de "próxima oleada", si está activo) y decide si es crítico.
 */
export function rollHeroHit(baseDamage: number, stats: RunStats, rng: () => number): HeroHit {
  const atkMul = 1 + (stats.atkPct + stats.nextWaveAtkPct) / 100;
  const isCrit = rng() < stats.critPct / 100;
  const damage = Math.max(1, Math.round(baseDamage * atkMul * (isCrit ? CRIT_MULTIPLIER : 1)));
  return { damage, isCrit };
}

/**
 * Resuelve un ciclo completo de ataque del héroe: dispara `projectileCount`
 * proyectiles y, si el doble ataque activa, repite la tanda completa una
 * vez más.
 */
export function resolveHeroAttack(
  baseDamage: number,
  stats: RunStats,
  rng: () => number
): HeroAttackResult {
  const volleys = rng() < stats.doubleAtkPct / 100 ? 2 : 1;
  const projectiles = projectileCount(stats);
  const hits: HeroHit[] = [];
  for (let v = 0; v < volleys; v++) {
    for (let p = 0; p < projectiles; p++) {
      hits.push(rollHeroHit(baseDamage, stats, rng));
    }
  }
  const totalDamage = hits.reduce((sum, hit) => sum + hit.damage, 0);
  return { hits, totalDamage };
}

export interface EnemyAttackResult {
  damage: number;
  missed: boolean;
  blocked: boolean;
}

/**
 * Resuelve el golpe melee de un enemigo contra el héroe: primero el fallo
 * (`enemyMissPct`), luego el bloqueo (`blockPct`) y, si ninguno aplica, la
 * armadura reduce el daño plano (con un piso de 1 para que siempre pique
 * algo).
 */
export function resolveEnemyAttack(
  baseDamage: number,
  stats: RunStats,
  rng: () => number
): EnemyAttackResult {
  if (rng() < stats.enemyMissPct / 100) {
    return { damage: 0, missed: true, blocked: false };
  }
  if (rng() < stats.blockPct / 100) {
    return { damage: 0, missed: false, blocked: true };
  }
  const damage = Math.max(1, Math.round(baseDamage - stats.armor));
  return { damage, missed: false, blocked: false };
}

/** Vida recuperada por robo de vida según el daño total infligido en un ciclo. */
export function computeLifesteal(totalDamageDealt: number, stats: RunStats): number {
  if (stats.lifestealPct <= 0 || totalDamageDealt <= 0) return 0;
  return Math.round(totalDamageDealt * (stats.lifestealPct / 100));
}
